import {
  PaymentPendingError,
  InsufficientPaymentAmountError,
  Session,
  waitForSession,
  Currency,
  Chain,
} from "@paywithglide/glide-js";
import ModalBackButton from "../components/ModalBackButton";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import PrimaryButton from "../components/PrimaryButton";
import { useEffect, useRef, useState, useMemo } from "react";
import Alert from "../components/Alert";
import WarningAlert from "../components/WarningAlert";
import {
  Clock,
  Copy,
  CheckCheck,
  CircleCheck,
  Undo2,
  MoreVertical,
} from "lucide-react";
import {
  bitcoin,
  bitcoinTestnet,
  lightning,
  solana,
  tron,
} from "@paywithglide/glide-js/chains";
import QRCode from "../components/QRCode";
import { LogoWithBadge } from "../components/LogoWithBadge";
import { logger } from "../lib/logger";
import {
  formatDisplayAmount,
  formatRemainingAmount,
} from "../lib/formatDisplayAmount";
import { subtractDecimalStrings } from "../lib/subtractDecimalStrings";
import { requiredPaymentAmount } from "../lib/requiredPaymentAmount";
import { getETA } from "../lib/getETAWarning";
import {
  isOverpaymentAboveThreshold,
  MIN_OVERPAYMENT_THRESHOLD_USD,
} from "../lib/overpayment";

type Props = {
  onBack: () => void;
  session: Session;
  bitcoinLightningSession?: Session;
  onSuccess: (session: Session) => void;
  onInsufficientPaymentAmount?: (session: Session) => void;
  onRequestRefund?: (session: Session) => void;
  onOverpaymentRefund?: (session: Session) => void;
  onSessionUpdate?: (session: Session) => void;
  selectedCurrency?: Currency;
  onShowSupportedChains?: (chains: Chain[]) => void;
  /**
   * Regenerates the deposit address without the stable deposit address key,
   * so the backend mints a fresh, non-deterministic address. When omitted,
   * the overflow menu is hidden entirely.
   */
  onRegenerateAddress?: () => Promise<void>;
};

export default function Transfer({
  onBack,
  session,
  bitcoinLightningSession,
  onSuccess,
  onInsufficientPaymentAmount,
  onRequestRefund,
  onOverpaymentRefund,
  onSessionUpdate,
  selectedCurrency,
  onShowSupportedChains,
  onRegenerateAddress,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();

  const isBitcoinSession =
    session.paymentChainId === bitcoin.id ||
    session.paymentChainId === bitcoinTestnet.id;

  // UX state for the new flow
  const [didUserConfirmPayment, setDidUserConfirmPayment] = useState(false);
  const [isPollingAborted, setIsPollingAborted] = useState(false);
  const [didCopyAddress, setDidCopyAddress] = useState(false);
  const [isAddressMenuOpen, setIsAddressMenuOpen] = useState(false);
  const [isRegeneratingAddress, setIsRegeneratingAddress] = useState(false);
  const [didCopyAmount, setDidCopyAmount] = useState(false);
  const [didCopyRemaining, setDidCopyRemaining] = useState(false);
  const [useLightningOnly, setUseLightningOnly] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string>("unpaid");
  const [incompletePollingError, setIncompletePollingError] = useState(false);

  const isIncompletePayment =
    depositConfig.mode === "pay" &&
    !!session.actualPaymentAmount &&
    Number(session.actualPaymentAmount) > 0 &&
    !!requiredPaymentAmount(session) &&
    Number(session.actualPaymentAmount) <
      Number(requiredPaymentAmount(session));

  const abortControllerRef = useRef<AbortController>();
  const didTriggerOverpaymentRef = useRef(false);

  const copyAddress = async () => {
    await navigator.clipboard.writeText(displayAddress);
    setDidCopyAddress(true);
    setTimeout(() => setDidCopyAddress(false), 2000);
  };

  const handleRegenerateAddress = async () => {
    if (!onRegenerateAddress || isRegeneratingAddress) return;
    setIsAddressMenuOpen(false);
    setIsRegeneratingAddress(true);
    try {
      await onRegenerateAddress();
    } finally {
      setIsRegeneratingAddress(false);
    }
  };

  // For Bitcoin, check if Lightning is available
  const shouldWaitForLightning =
    isBitcoinSession &&
    bitcoinLightningSession &&
    !depositConfig.excludeChainIds?.includes(lightning.id);

  // Get supported chains for the selected currency (if provided)
  const supportedChains = useMemo(() => {
    if (!selectedCurrency) return [];

    const chains: Chain[] = [];

    // Check Ethereum chains
    const evmChains = glideConfig.chains.filter((chain) => {
      try {
        const caip19 = selectedCurrency.on(chain);
        return caip19.startsWith("eip155:") && !chain.isTestnet;
      } catch {
        return false;
      }
    });
    chains.push(...evmChains);

    // Check Solana
    try {
      selectedCurrency.on(solana);
      chains.push(solana);
    } catch {
      // Not supported
    }

    // Check Bitcoin
    try {
      selectedCurrency.on(bitcoin);
      chains.push(bitcoin);
    } catch {
      // Not supported
    }

    // Check Tron
    try {
      selectedCurrency.on(tron);
      chains.push(tron);
    } catch {
      // Not supported
    }

    return chains;
  }, [selectedCurrency, glideConfig.chains]);

  // Get supported chain logos (deduplicated by logoUrl for Ethereum)
  const supportedChainLogos = useMemo(() => {
    const uniqueLogos = new Map<string, string>();
    supportedChains.forEach((chain) => {
      if (chain.logoUrl && !uniqueLogos.has(chain.logoUrl)) {
        uniqueLogos.set(chain.logoUrl, chain.logoUrl);
      }
    });
    return Array.from(uniqueLogos.values());
  }, [supportedChains]);

  // Determine if selected currency is available on only one chain
  const isSingleChainCurrency = useMemo(() => {
    if (!selectedCurrency) return false;

    let totalChainCount = 0;

    // Check Ethereum chains
    const evmChains = glideConfig.chains.filter((chain) => {
      try {
        const caip19 = selectedCurrency.on(chain);
        return caip19.startsWith("eip155:") && !chain.isTestnet;
      } catch {
        return false;
      }
    });
    totalChainCount += evmChains.length;

    // Check Solana
    try {
      selectedCurrency.on(solana);
      totalChainCount += 1;
    } catch {
      // Not supported
    }

    // Check Bitcoin
    try {
      selectedCurrency.on(bitcoin);
      totalChainCount += 1;
    } catch {
      // Not supported
    }

    // Check Tron
    try {
      selectedCurrency.on(tron);
      totalChainCount += 1;
    } catch {
      // Not supported
    }

    return totalChainCount === 1;
  }, [selectedCurrency, glideConfig.chains]);

  const tokenPriceUSD = useMemo(() => {
    const paymentAmount = Number(session.paymentAmount);
    const paymentAmountUSD = Number(session.paymentAmountUSD);
    if (paymentAmount > 0 && paymentAmountUSD > 0) {
      return paymentAmountUSD / paymentAmount;
    }
    return undefined;
  }, [session.paymentAmount, session.paymentAmountUSD]);

  const formattedPaymentAmount = useMemo(() => {
    return formatDisplayAmount(session.paymentAmount, tokenPriceUSD);
  }, [session.paymentAmount, tokenPriceUSD]);

  useEffect(() => {
    if (abortControllerRef.current) return;

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const startPolling = async () => {
      try {
        const waitPromises = [
          waitForSession(glideConfig, {
            sessionId: session.sessionId,
            signal: abortController.signal,
            onUpdate: (updatedSession) => {
              setPaymentStatus(updatedSession.paymentStatus);

              if (updatedSession.paymentStatus === "paid") {
                onSuccess(updatedSession);
              }

              // Detect partial payment — stay on this screen with inline alert
              if (
                depositConfig.mode === "pay" &&
                updatedSession.paymentStatus === "unpaid" &&
                updatedSession.actualPaymentAmount &&
                Number(updatedSession.actualPaymentAmount) > 0 &&
                updatedSession.paymentAmount
              ) {
                const paid = Number(updatedSession.actualPaymentAmount);
                const required = Number(requiredPaymentAmount(updatedSession));
                if (paid < required) {
                  abortController.abort();
                  onSessionUpdate?.(updatedSession);
                  return;
                }
              }

              if (
                isBitcoinSession &&
                updatedSession.paymentStatus === "waiting_confirmations"
              ) {
                onSuccess(updatedSession);
              }
              if (
                depositConfig.mode === "pay" &&
                updatedSession.paymentStatus === "pending_refund" &&
                onInsufficientPaymentAmount
              ) {
                abortController.abort();
                onInsufficientPaymentAmount(updatedSession);
              }

              // Detect overpayment - only in pay mode, only after funds arrive
              const isFunded =
                updatedSession.paymentStatus === "waiting_confirmations" ||
                updatedSession.paymentStatus === "paid";

              if (
                depositConfig.mode === "pay" &&
                isFunded &&
                onOverpaymentRefund &&
                updatedSession.paymentAmountUSD &&
                updatedSession.actualPaymentAmountUSD &&
                !didTriggerOverpaymentRef.current
              ) {
                const requiredAmount = Number(updatedSession.paymentAmountUSD);
                const paidAmount = Number(
                  updatedSession.actualPaymentAmountUSD,
                );
                const deltaAmount = paidAmount - requiredAmount;
                const shouldTrigger = isOverpaymentAboveThreshold(
                  requiredAmount,
                  paidAmount,
                );
                logger.info(
                  `[OverpaymentCheck][Transfer] sessionId=${updatedSession.sessionId} status=${updatedSession.paymentStatus} requiredUSD=${requiredAmount} paidUSD=${paidAmount} deltaUSD=${deltaAmount} thresholdUSD=${MIN_OVERPAYMENT_THRESHOLD_USD} trigger=${shouldTrigger}`,
                );

                if (shouldTrigger) {
                  didTriggerOverpaymentRef.current = true;
                  onOverpaymentRefund(updatedSession);
                }
              }

              window.parent?.postMessage(
                { type: "sessionUpdate", session: updatedSession },
                "*",
              );
            },
          }),
        ];

        if (bitcoinLightningSession) {
          waitPromises.push(
            waitForSession(glideConfig, {
              sessionId: bitcoinLightningSession.sessionId,
              signal: abortController.signal,
              onUpdate: (updatedSession) => {
                setPaymentStatus(updatedSession.paymentStatus);

                if (updatedSession.paymentStatus === "paid") {
                  onSuccess(updatedSession);
                }
                if (updatedSession.paymentStatus === "waiting_confirmations") {
                  onSuccess(updatedSession);
                }
                if (
                  depositConfig.mode === "pay" &&
                  updatedSession.paymentStatus === "pending_refund" &&
                  onInsufficientPaymentAmount
                ) {
                  abortController.abort();
                  onInsufficientPaymentAmount(updatedSession);
                }

                // Detect overpayment - only in pay mode, only after funds arrive
                const isFunded =
                  updatedSession.paymentStatus === "waiting_confirmations" ||
                  updatedSession.paymentStatus === "paid";

                if (
                  depositConfig.mode === "pay" &&
                  isFunded &&
                  onOverpaymentRefund &&
                  updatedSession.paymentAmountUSD &&
                  updatedSession.actualPaymentAmountUSD &&
                  !didTriggerOverpaymentRef.current
                ) {
                  const requiredAmount = Number(
                    updatedSession.paymentAmountUSD,
                  );
                  const paidAmount = Number(
                    updatedSession.actualPaymentAmountUSD,
                  );
                  const deltaAmount = paidAmount - requiredAmount;
                  const shouldTrigger = isOverpaymentAboveThreshold(
                    requiredAmount,
                    paidAmount,
                  );
                  logger.info(
                    `[OverpaymentCheck][Transfer/Lightning] sessionId=${updatedSession.sessionId} status=${updatedSession.paymentStatus} requiredUSD=${requiredAmount} paidUSD=${paidAmount} deltaUSD=${deltaAmount} thresholdUSD=${MIN_OVERPAYMENT_THRESHOLD_USD} trigger=${shouldTrigger}`,
                  );

                  if (shouldTrigger) {
                    didTriggerOverpaymentRef.current = true;
                    onOverpaymentRefund(updatedSession);
                  }
                }

                window.parent?.postMessage(
                  { type: "sessionUpdate", session: updatedSession },
                  "*",
                );
              },
            }),
          );
        }

        await Promise.race(waitPromises);
      } catch (e) {
        if (e instanceof PaymentPendingError) {
          // ignore
        } else {
          logger.error(`${e}`);
        }
      }
    };

    startPolling();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = undefined;
      }
    };
  }, [
    onSuccess,
    session.sessionId,
    glideConfig,
    session.paymentChainId,
    bitcoinLightningSession,
    isBitcoinSession,
    session,
    onInsufficientPaymentAmount,
    onOverpaymentRefund,
    onSessionUpdate,
    depositConfig.mode,
  ]);
  const displayAddress = isBitcoinSession
    ? useLightningOnly && shouldWaitForLightning
      ? bitcoinLightningSession?.depositAddress || ""
      : session.depositAddress || ""
    : session.depositAddress || "";

  // QR code value: plain address for Bitcoin (toggle between on-chain and Lightning)
  const qrCodeValue = isBitcoinSession
    ? useLightningOnly && shouldWaitForLightning
      ? bitcoinLightningSession?.depositAddress || ""
      : session.depositAddress || ""
    : session.depositAddress || "";

  // Incomplete payment: computed values
  const incompletePaidStr = session.actualPaymentAmount || "0";
  const incompleteRequiredStr = requiredPaymentAmount(session) || "0";
  const incompleteRemainingStr = isIncompletePayment
    ? subtractDecimalStrings(incompleteRequiredStr, incompletePaidStr)
    : "0";
  const incompleteCurrencySymbol = isIncompletePayment
    ? session.actualPaymentCurrencySymbol || session.paymentCurrencySymbol
    : "";

  const formattedIncompleteRemaining = useMemo(() => {
    return formatRemainingAmount(
      incompleteRemainingStr,
      Number(session.actualPaymentAmountUSD) / Number(incompletePaidStr),
    );
  }, [
    incompleteRemainingStr,
    session.actualPaymentAmountUSD,
    incompletePaidStr,
  ]);

  // Incomplete payment: continue polling for the remaining funds
  useEffect(() => {
    if (!isIncompletePayment) return;

    const abortController = new AbortController();
    let didCallSuccess = false;

    waitForSession(glideConfig, {
      sessionId: session.sessionId,
      signal: abortController.signal,
      onUpdate: (updatedSession) => {
        if (updatedSession.paymentStatus === "paid") {
          didCallSuccess = true;
          onSuccess(updatedSession);
        }
        if (updatedSession.paymentStatus === "pending_refund") {
          abortController.abort();
          onInsufficientPaymentAmount?.(updatedSession);
        }
      },
    })
      .then((completedSession) => {
        if (!didCallSuccess) {
          onSuccess(completedSession);
        }
      })
      .catch((error) => {
        if (error instanceof InsufficientPaymentAmountError) {
          onInsufficientPaymentAmount?.(session);
          return;
        }
        if (error?.name === "AbortError") return;
        logger.error(`[Transfer/IncompletePayment] Polling error: ${error}`);
        setIncompletePollingError(true);
      });

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncompletePayment]);

  return (
    <>
      {!isIncompletePayment && <ModalBackButton onClick={() => onBack()} />}

      {/* Amount header */}
      <div className="mt-2">
        <div className="flex flex-col items-center justify-center">
          <div className="pb-1 text-sm font-medium text-[--color-text-primary]">
            {isBitcoinSession ? "Pay" : `Pay on ${session.paymentChainName}`}
          </div>

          {isIncompletePayment ? (
            <button
              className="flex items-center gap-2 px-2 py-1 cursor-pointer"
              onClick={async () => {
                await navigator.clipboard.writeText(incompleteRemainingStr);
                setDidCopyRemaining(true);
                setTimeout(() => setDidCopyRemaining(false), 2000);
              }}
              aria-label="Copy remaining amount"
            >
              {(session.actualPaymentCurrencyLogoUrl ||
                session.paymentCurrencyLogoUrl) &&
                (session.actualPaymentChainLogoUrl ||
                  session.paymentChainLogoUrl) && (
                  <LogoWithBadge
                    logoUrl={
                      session.actualPaymentCurrencyLogoUrl ||
                      session.paymentCurrencyLogoUrl
                    }
                    badgeUrl={
                      session.actualPaymentChainLogoUrl ||
                      session.paymentChainLogoUrl
                    }
                  />
                )}
              <span className="font-bold text-lg text-[--color-text-primary]">
                {formattedIncompleteRemaining} {incompleteCurrencySymbol}
              </span>
              {didCopyRemaining ? (
                <CheckCheck className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-[--color-text-secondary]" />
              )}
            </button>
          ) : (
            <button
              className="flex items-center gap-2 px-2 py-1 cursor-pointer group"
              onClick={async () => {
                await navigator.clipboard.writeText(
                  `${formattedPaymentAmount} ${session.paymentCurrencySymbol}`,
                );
                setDidCopyAmount(true);
                setTimeout(() => setDidCopyAmount(false), 2000);
              }}
              aria-label="Copy amount"
            >
              {session.paymentCurrencyLogoUrl &&
                session.paymentChainLogoUrl && (
                  <LogoWithBadge
                    logoUrl={session.paymentCurrencyLogoUrl}
                    badgeUrl={session.paymentChainLogoUrl}
                  />
                )}
              <span className="font-bold text-lg text-[--color-text-primary]">
                {formattedPaymentAmount} {session.paymentCurrencySymbol}
              </span>
              {didCopyAmount ? (
                <CheckCheck className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-[--color-text-secondary]" />
              )}
            </button>
          )}
        </div>
      </div>

      {!isIncompletePayment && (
        <div className="mt-2 text-sm text-[--color-text-secondary] text-left">
          {isBitcoinSession
            ? "Send your payment to this address"
            : `Send your payment to this address on ${session.paymentChainName}`}
        </div>
      )}

      {/* QR + Address row */}
      <div className="mt-3 flex items-start gap-3">
        <div className="flex-shrink-0">
          <div className="w-[118px] h-[118px] rounded-lg shadow-sm bg-white flex items-center justify-center relative overflow-hidden">
            <div className="relative w-full h-full p-0.5">
              <QRCode
                value={qrCodeValue}
                size={
                  isBitcoinSession
                    ? useLightningOnly && shouldWaitForLightning
                      ? 75
                      : 88
                    : 100
                }
                dotsType="dots"
                errorCorrectionLevel={isBitcoinSession ? "L" : "Q"}
              />
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div
            className={`bg-[--color-background-secondary] rounded-lg p-2 flex flex-col shadow-sm ${
              isBitcoinSession && shouldWaitForLightning
                ? "h-[82px]"
                : "h-[118px]"
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-[--color-text-secondary] text-xs">
                {isBitcoinSession && useLightningOnly && shouldWaitForLightning
                  ? "Lightning Invoice"
                  : "Payment Address"}
              </h4>
              {/* Overflow menu: edge-case action, kept away from the copy target */}
              {onRegenerateAddress && (
                <div className="relative flex-shrink-0">
                  <button
                    className="p-0.5 rounded-md hover:bg-[--color-background-tertiary] transition-colors duration-200 disabled:opacity-50"
                    onClick={() => setIsAddressMenuOpen((open) => !open)}
                    disabled={isRegeneratingAddress}
                    aria-label="Address options"
                    aria-haspopup="menu"
                    aria-expanded={isAddressMenuOpen}
                  >
                    <MoreVertical className="w-4 h-4 text-[--color-text-secondary]" />
                  </button>
                  {isAddressMenuOpen && (
                    <>
                      {/* Transparent backdrop closes the menu on any outside click */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsAddressMenuOpen(false)}
                      />
                      <div
                        role="menu"
                        className="absolute right-full mr-1 top-1/2 -translate-y-1/2 bg-[--color-modal-background] border border-[--color-background-secondary] rounded-lg shadow-lg z-50 overflow-hidden"
                      >
                        <button
                          role="menuitem"
                          onClick={handleRegenerateAddress}
                          className="py-2 px-4 text-xs text-[--color-text-primary] hover:bg-[--color-background-secondary] transition-colors whitespace-nowrap w-full text-left"
                        >
                          {isRegeneratingAddress
                            ? "Generating…"
                            : "Generate new address"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              <div className="flex items-start justify-between gap-2 h-full">
                <div className="flex-1 pr-2 overflow-auto">
                  <button
                    type="button"
                    onClick={copyAddress}
                    aria-label="Copy payment address"
                    className="text-left group w-full"
                  >
                    <span className="font-mono text-xs text-[--color-text-primary] break-all leading-relaxed">
                      {displayAddress}
                      <span className="inline-flex items-center align-middle ml-1 p-0.5 rounded-md relative -top-0.5 whitespace-nowrap">
                        {didCopyAddress ? (
                          <CheckCheck className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-[--color-text-secondary] group-hover:text-[--color-text-primary] transition-colors" />
                        )}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Lightning toggle for Bitcoin - separate box below */}
          {isBitcoinSession && shouldWaitForLightning && (
            <div className="bg-[--color-background-secondary] rounded-lg px-3 py-1.5 shadow-sm flex items-center justify-between">
              <span className="text-xs text-[--color-text-secondary] font-medium">
                Lightning
              </span>
              <button
                onClick={() => setUseLightningOnly(!useLightningOnly)}
                className="flex items-center"
              >
                <div
                  className={`w-8 h-4 rounded-full transition-colors ${useLightningOnly ? "bg-blue-500" : "bg-gray-300"} relative`}
                >
                  <div
                    className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${useLightningOnly ? "translate-x-4" : "translate-x-0.5"}`}
                  />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Supported chains component - show only when there are multiple chains (hide for single chain currencies) */}
      {!isSingleChainCurrency && supportedChainLogos.length > 1 && (
        <div className="mt-4">
          <div className="text-xs text-[--color-text-secondary] mb-2">
            {selectedCurrency
              ? `${selectedCurrency.symbol} is available on`
              : "Supported chains"}
          </div>
          <button
            className="flex items-center gap-2 overflow-hidden rounded-lg border border-[--color-text-secondary]/30 p-1 w-full hover:bg-[--color-background-secondary]/50 transition-colors group justify-start pl-2"
            onClick={() => {
              if (supportedChains.length > 0 && onShowSupportedChains) {
                onShowSupportedChains(supportedChains);
              }
            }}
            aria-label="View supported chains"
          >
            <div className="flex py-2 items-center">
              {supportedChainLogos.map((logoUrl, index) => (
                <span
                  key={index}
                  className="inline-flex items-center transition-all duration-200 mr-2"
                >
                  <img
                    src={logoUrl}
                    className="h-5 w-5 rounded-full transition-opacity duration-200"
                    alt="Chain logo"
                    loading="eager"
                    onLoad={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                    onError={(e) => {
                      e.currentTarget.style.opacity = "0.5";
                      e.currentTarget.style.backgroundColor = "#f3f4f6";
                    }}
                    style={{ opacity: 0 }}
                  />
                </span>
              ))}
            </div>
          </button>
        </div>
      )}

      {isIncompletePayment ? (
        <WarningAlert
          error={
            incompletePollingError ? "Please refresh and try again." : undefined
          }
          action={{
            label: "Cancel and request a refund",
            icon: <Undo2 className="w-3 h-3 flex-shrink-0" />,
            onAction: () => onRequestRefund?.(session),
          }}
        >
          <div className="font-semibold mb-1">Partial Payment Detected</div>
          <div>
            We detected a payment of{" "}
            <span className="font-semibold">
              {formatDisplayAmount(incompletePaidStr)}{" "}
              {incompleteCurrencySymbol}
            </span>
            . Please send the remaining amount to the same address to complete
            your payment.
          </div>
        </WarningAlert>
      ) : (
        <>
          <div className="flex flex-col gap-2 items-center mt-4">
            {!didUserConfirmPayment && (
              <PrimaryButton
                className="mt-4"
                onClick={() => {
                  setDidUserConfirmPayment(true);
                  if (isPollingAborted) setIsPollingAborted(false);
                }}
                disabled={!session.depositAddress}
              >
                Payment Complete
              </PrimaryButton>
            )}

            {didUserConfirmPayment &&
            !isPollingAborted &&
            paymentStatus === "unpaid" ? (
              <Alert
                icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                title={
                  depositConfig.mode === "deposit"
                    ? "Waiting for deposit.."
                    : "Waiting for payment.."
                }
                subtitle={
                  depositConfig.mode === "deposit"
                    ? "We're waiting for your deposit. This can take a minute after you have initiated it."
                    : "We're waiting for your payment. This can take a minute after you have initiated it."
                }
              />
            ) : didUserConfirmPayment &&
              !isPollingAborted &&
              paymentStatus === "waiting_confirmations" ? (
              <Alert
                icon={<CircleCheck strokeWidth={2.25} className="w-4 h-4" />}
                title={
                  depositConfig.mode === "deposit"
                    ? "Deposit received"
                    : "Payment received"
                }
                subtitle={`Confirming your ${
                  depositConfig.mode === "deposit" ? "deposit" : "payment"
                }. ${
                  session.etaInSeconds > 10
                    ? `This can take up to ${getETA(session.etaInSeconds)}.`
                    : "This takes a few seconds."
                }`}
              />
            ) : null}
          </div>
        </>
      )}
    </>
  );
}
